import { styled } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { FormattedMessage, useIntl } from "react-intl";

import { IconButton } from "@/common/components/buttons/icon-button/IconButton";
import { Icon } from "@/shared/components/common/Icon/Icon";

import { PrimaryButton } from "../buttons/primary-button/primary-button";

import { ErrorMessage } from "./shared.styles";

type FieldErrorProps = {
  error?: React.ReactNode;
  name?: string;
};

const FieldError = ({
  error,
  name,
}: FieldErrorProps): React.ReactElement | null => {
  if (!error) return null;
  return (
    <ErrorMessage data-testid={name ? `${name}-error` : undefined}>
      {typeof error === "string" ? <FormattedMessage id={error} /> : error}
    </ErrorMessage>
  );
};

type IconOrSpinnerProps = {
  icon: React.ReactNode;
  loading?: boolean;
};

const IconOrSpinner = ({
  icon,
  loading,
}: IconOrSpinnerProps): React.ReactElement => {
  if (loading) return <CircularProgress color="inherit" size={20} />;
  return <>{icon}</>;
};

type FieldClearButtonProps = {
  name: string;
  disabled?: boolean;
  onClick: (e: React.MouseEvent) => void;
};

const FieldClearButton = ({
  disabled,
  name,
  onClick,
}: FieldClearButtonProps): React.ReactElement => {
  const { formatMessage } = useIntl();

  return (
    <ClearButton
      aria-label={formatMessage({ id: "common.clear" })}
      data-testid={`${name}-clear`}
      disabled={disabled}
      onClick={onClick}
      onMouseDown={(e) => e.preventDefault()}
      type="button"
    >
      <Icon name="close" />
    </ClearButton>
  );
};

type DropdownNoOptionsProps = {
  name: string;
};

const DropdownNoOptions = ({
  name,
}: DropdownNoOptionsProps): React.ReactElement => (
  <NoOptions data-testid={`${name}-no-options`}>
    <FormattedMessage id="common.noSearchResults" />
  </NoOptions>
);

type DropdownSearchingProps = {
  name: string;
};

const DropdownSearching = ({
  name,
}: DropdownSearchingProps): React.ReactElement => (
  <NoOptions data-testid={`${name}-searching`}>
    <SearchingRow>
      <CircularProgress color="inherit" size={12} />
      <FormattedMessage id="common.loading" />
    </SearchingRow>
  </NoOptions>
);

type FieldRetryButtonProps = {
  name: string;
  onClick: () => void;
};

const FieldRetryButton = ({
  name,
  onClick,
}: FieldRetryButtonProps): React.ReactElement => (
  <RetryButton
    data-testid={`${name}-retry`}
    onClick={onClick}
    type="button"
    variant="text"
  >
    <Icon name="refresh" size="sm" />
    <FormattedMessage id="ErrorBox.retryButton" />
  </RetryButton>
);

const ClearButton = styled(IconButton)(({ theme }) => ({
  width: "20px",
  height: "20px",
  fontSize: "20px",
  color: theme.palette.cf.cfPrimary,
  "&:disabled": {
    color: theme.palette.cf.cfDarkGrey,
    cursor: "not-allowed",
  },
}));

const NoOptions = styled("div")(({ theme }) => ({
  padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
  fontSize: "14px",
  color: theme.palette.cf.cfDarkerGrey,
}));

const SearchingRow = styled("span")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const RetryButton = styled(PrimaryButton)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  background: "none",
  border: "none",
  padding: 0,
  color: theme.palette.cf.cfPrimary,
  fontSize: "12px",
  lineHeight: "12px",
  cursor: "pointer",
  fontWeight: "normal",
  "& svg": {
    height: "12px",
    width: "12px",
  },
}));

export {
  DropdownNoOptions,
  DropdownSearching,
  FieldClearButton,
  FieldError,
  FieldRetryButton,
  IconOrSpinner,
};
